import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/components/site/Reveal";
import { StudioFrame } from "@/components/site/StudioFrame";
import { HERO_IMG } from "@/lib/site-data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const tankY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="top"
      className="grain relative flex min-h-screen items-center overflow-hidden pt-28"
    >
      {/* backdrop glow */}
      <div
        className="pointer-events-none absolute -right-1/4 top-1/4 h-[600px] w-[600px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(229,57,53,0.12), transparent 62%)" }}
      />
      <div
        className="pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(7,28,63,0.32), transparent 62%)" }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-[1400px] grid-cols-1 items-center gap-10 px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-12">
        <motion.div style={{ y: textY, opacity: fade }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-avatar-ink/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-avatar-ink/60"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-avatar-gold" />
            ISO Certified · 5-Layer Technology
          </motion.div>

          <h1 className="font-heading text-5xl font-bold leading-[0.92] tracking-tighter text-avatar-ink sm:text-6xl lg:text-[5.5rem]">
            <MaskedLines lines={["Built with Trust."]} />
            <MaskedLines
              lines={["Engineered to Last."]}
              delay={0.15}
              lineClassName="text-avatar-red"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-avatar-ink/70 md:text-lg"
          >
            Advanced water storage solutions with 5-Layer Protection for pure,
            safe and hygienic water — engineered for modern homes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("#products")}
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-avatar-ink px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-avatar-red"
            >
              Explore Products
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("#dealers")}
              data-testid="hero-dealer-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-avatar-ink/25 px-8 py-4 text-sm font-semibold text-avatar-ink transition-colors duration-300 hover:bg-avatar-red hover:border-avatar-red hover:text-white"
            >
              Become a Dealer
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: tankY }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <StudioFrame
              src={HERO_IMG}
              alt="The Avatar Pure water tank — mastering the purity"
              glow="#E53935"
              blend={false}
              dark
              float
              className="mx-auto max-w-md"
              rounded="rounded-3xl"
            />
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-avatar-ink/40"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <div className="h-10 w-px bg-gradient-to-b from-avatar-ink/40 to-transparent" />
      </motion.div>
    </section>
  );
};
